import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import {
  Input,
  Select,
  Table,
  Button,
  Space,
} from 'antd';
import * as api from 'util/api';

/** Sentinel for empty Matrix format fields in SKU picker filters. */
const SKU_FILTER_UNSPECIFIED = '__sku_filter_unspecified__';

function skuFormatValue(sku, camelKey, snakeKey) {
  const raw = sku[camelKey] ?? sku[snakeKey];
  if (raw == null || String(raw).trim() === '') return SKU_FILTER_UNSPECIFIED;
  return String(raw);
}

function buildSkuFilterOptions(values) {
  const unique = [...new Set(values)];
  unique.sort((a, b) => {
    if (a === SKU_FILTER_UNSPECIFIED) return -1;
    if (b === SKU_FILTER_UNSPECIFIED) return 1;
    return String(a).localeCompare(String(b), undefined, { numeric: true });
  });
  return unique.map((v) => ({
    value: v,
    label: v === SKU_FILTER_UNSPECIFIED ? 'Unspecified' : v,
  }));
}

function skuMatchesMultiFilter(selected, sku, camelKey, snakeKey) {
  if (!selected.length) return true;
  const v = skuFormatValue(sku, camelKey, snakeKey);
  return selected.includes(v);
}

function buildRangeByCin7Sku(cacheRows) {
  const m = {};
  (cacheRows || []).forEach((r) => {
    const sku = r.cin7_sku ?? r.cin7Sku;
    if (sku == null || String(sku).trim() === '') return;
    const key = String(sku).trim();
    const a3 = r.additional_attribute_3 ?? r.additionalAttribute3;
    m[key] = a3 != null && String(a3).trim() !== '' ? String(a3).trim() : null;
  });
  return m;
}

function skuProductRangeFilterValue(sku, rangeByCin7Sku) {
  const cin7 = sku.cin7Sku ?? sku.cin7_sku;
  if (cin7 == null || String(cin7).trim() === '') return SKU_FILTER_UNSPECIFIED;
  const r = rangeByCin7Sku[String(cin7).trim()];
  if (r == null || String(r).trim() === '') return SKU_FILTER_UNSPECIFIED;
  return String(r).trim();
}

/**
 * SKU picker: loads all product SKUs and lets the user select which to add.
 * @param {Set} existingSkuKeys
 * @param {Array} selectedKeys
 * @param {function} onSelectionChange
 * @param {string} searchTerm
 * @param {function} onSearchChange
 * @param {Array|undefined} cin7CachedProducts — when set, Cin7 product range is resolved
 *   from this data without a separate fetch.
 */
const SkuPicker = ({
  existingSkuKeys,
  selectedKeys,
  onSelectionChange,
  searchTerm,
  onSearchChange,
  cin7CachedProducts,
}) => {
  const [skus, setSkus] = useState([]);
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(true);
  const [internalCin7Cache, setInternalCin7Cache] = useState(null);
  const [rangeOptionsList, setRangeOptionsList] = useState([]);
  const [filterContainerType, setFilterContainerType] = useState([]);
  const [filterUnitType, setFilterUnitType] = useState([]);
  const [filterPackType, setFilterPackType] = useState([]);
  const [filterCaseType, setFilterCaseType] = useState([]);
  const [filterProductRange, setFilterProductRange] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [skuData, productData, ranges] = await Promise.all([
          api.getAllProductSkus(),
          api.getProducts(),
          api.listPricingRanges().catch(() => []),
        ]);
        setSkus(Array.isArray(skuData) ? skuData : []);
        const pMap = {};
        (Array.isArray(productData) ? productData : []).forEach((p) => {
          pMap[p.id || p.key] = p.name || p.beerName || '';
        });
        setProducts(pMap);
        setRangeOptionsList(Array.isArray(ranges) ? ranges : []);
      } catch {
        setSkus([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (Array.isArray(cin7CachedProducts)) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getCachedCin7Products();
        if (!cancelled) setInternalCin7Cache(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setInternalCin7Cache([]);
      }
    })();
    return () => { cancelled = true; };
  }, [cin7CachedProducts]);

  const cacheRows = Array.isArray(cin7CachedProducts) ? cin7CachedProducts : internalCin7Cache;
  const rangeByCin7Sku = useMemo(() => buildRangeByCin7Sku(cacheRows), [cacheRows]);

  const rangeDataLoading = !Array.isArray(cin7CachedProducts) && internalCin7Cache === null;

  const availableSkus = useMemo(
    () => skus.filter((s) => !existingSkuKeys.has(s.key)),
    [skus, existingSkuKeys],
  );

  const containerTypeOptions = useMemo(
    () => buildSkuFilterOptions(availableSkus.map((s) => skuFormatValue(s, 'containerType', 'container_type'))),
    [availableSkus],
  );
  const unitTypeOptions = useMemo(
    () => buildSkuFilterOptions(availableSkus.map((s) => skuFormatValue(s, 'unitType', 'unit_type'))),
    [availableSkus],
  );
  const packTypeOptions = useMemo(
    () => buildSkuFilterOptions(availableSkus.map((s) => skuFormatValue(s, 'packType', 'pack_type'))),
    [availableSkus],
  );
  const caseTypeOptions = useMemo(
    () => buildSkuFilterOptions(availableSkus.map((s) => skuFormatValue(s, 'caseType', 'case_type'))),
    [availableSkus],
  );

  const productRangeFilterOptions = useMemo(() => [
    { value: SKU_FILTER_UNSPECIFIED, label: 'Unspecified' },
    ...rangeOptionsList.map((r) => ({ value: r, label: r })),
  ], [rangeOptionsList]);

  const skuMatchesProductRangeFilter = useCallback((selected, sku) => {
    if (!selected.length) return true;
    const v = skuProductRangeFilterValue(sku, rangeByCin7Sku);
    return selected.includes(v);
  }, [rangeByCin7Sku]);

  const filtered = useMemo(() => availableSkus.filter((s) => {
    if (!skuMatchesMultiFilter(filterContainerType, s, 'containerType', 'container_type')) return false;
    if (!skuMatchesMultiFilter(filterUnitType, s, 'unitType', 'unit_type')) return false;
    if (!skuMatchesMultiFilter(filterPackType, s, 'packType', 'pack_type')) return false;
    if (!skuMatchesMultiFilter(filterCaseType, s, 'caseType', 'case_type')) return false;
    if (!skuMatchesProductRangeFilter(filterProductRange, s)) return false;
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const pid = s.productMatrixId ?? s.product_matrix_id;
    const productName = (products[pid] || '').toLowerCase();
    const skuName = (s.name || '').toLowerCase();
    return productName.includes(term) || skuName.includes(term) || String(s.key).includes(term);
  }), [
    availableSkus,
    filterContainerType,
    filterUnitType,
    filterPackType,
    filterCaseType,
    filterProductRange,
    skuMatchesProductRangeFilter,
    searchTerm,
    products,
  ]);

  const handleSelectAllMatching = () => {
    const keys = new Set(selectedKeys);
    filtered.forEach((s) => keys.add(s.key));
    onSelectionChange([...keys]);
  };

  const handleClearSelection = () => {
    onSelectionChange([]);
  };

  const formatCell = (sku, camelKey, snakeKey) => {
    const v = sku[camelKey] ?? sku[snakeKey];
    if (v == null || String(v).trim() === '') return '—';
    return String(v);
  };

  const columns = [
    {
      title: 'Product',
      key: 'product',
      render: (_, record) => {
        const pid = record.productMatrixId ?? record.product_matrix_id;
        return products[pid] || `#${pid}`;
      },
    },
    { title: 'SKU Name', dataIndex: 'name', key: 'name' },
    {
      title: 'Range',
      key: 'productRange',
      width: 100,
      ellipsis: true,
      render: (_, record) => {
        const cin7 = record.cin7Sku ?? record.cin7_sku;
        if (!cin7 || String(cin7).trim() === '') return '—';
        const r = rangeByCin7Sku[String(cin7).trim()];
        if (r == null || String(r).trim() === '') return '—';
        return String(r);
      },
    },
    {
      title: 'Container',
      key: 'containerType',
      width: 100,
      render: (_, record) => formatCell(record, 'containerType', 'container_type'),
    },
    {
      title: 'Unit size',
      key: 'unitType',
      width: 88,
      render: (_, record) => formatCell(record, 'unitType', 'unit_type'),
    },
    {
      title: 'Pack',
      key: 'packType',
      width: 88,
      render: (_, record) => formatCell(record, 'packType', 'pack_type'),
    },
    {
      title: 'Case',
      key: 'caseType',
      width: 88,
      render: (_, record) => formatCell(record, 'caseType', 'case_type'),
    },
    {
      title: 'Cin7',
      key: 'cin7Sku',
      width: 140,
      ellipsis: true,
      render: (_, record) => record.cin7Sku ?? record.cin7_sku ?? '',
    },
  ];

  const selectFilterProps = {
    mode: 'multiple',
    allowClear: true,
    showSearch: true,
    optionFilterProp: 'label',
    maxTagCount: 'responsive',
    style: { minWidth: 140, flex: '1 1 140px' },
  };

  const tableLoading = loading || rangeDataLoading;

  return (
    <div>
      <Input.Search
        placeholder="Search by product or SKU name..."
        value={searchTerm}
        onChange={(e) => onSearchChange(e.target.value)}
        style={{ marginBottom: 12 }}
        allowClear
      />
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 12,
        }}
      >
        <Select
          {...selectFilterProps}
          placeholder="Product range"
          value={filterProductRange}
          onChange={setFilterProductRange}
          options={productRangeFilterOptions}
        />
        <Select
          {...selectFilterProps}
          placeholder="Container type"
          value={filterContainerType}
          onChange={setFilterContainerType}
          options={containerTypeOptions}
        />
        <Select
          {...selectFilterProps}
          placeholder="Unit size"
          value={filterUnitType}
          onChange={setFilterUnitType}
          options={unitTypeOptions}
        />
        <Select
          {...selectFilterProps}
          placeholder="Pack type"
          value={filterPackType}
          onChange={setFilterPackType}
          options={packTypeOptions}
        />
        <Select
          {...selectFilterProps}
          placeholder="Case type"
          value={filterCaseType}
          onChange={setFilterCaseType}
          options={caseTypeOptions}
        />
      </div>
      <Space wrap style={{ marginBottom: 12 }}>
        <Button type="default" onClick={handleSelectAllMatching} disabled={!filtered.length}>
          Select all matching filters
        </Button>
        <Button type="default" onClick={handleClearSelection} disabled={!selectedKeys.length}>
          Clear selection
        </Button>
      </Space>
      <Table
        dataSource={filtered}
        columns={columns}
        rowKey="key"
        loading={tableLoading}
        size="small"
        pagination={{ pageSize: 10, showSizeChanger: false }}
        rowSelection={{
          selectedRowKeys: selectedKeys,
          onChange: onSelectionChange,
          preserveSelectedRowKeys: true,
        }}
        scroll={{ x: 'max-content', y: 400 }}
      />
    </div>
  );
};

SkuPicker.propTypes = {
  existingSkuKeys: PropTypes.instanceOf(Set).isRequired,
  selectedKeys: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ).isRequired,
  onSelectionChange: PropTypes.func.isRequired,
  searchTerm: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  cin7CachedProducts: PropTypes.arrayOf(PropTypes.object),
};

SkuPicker.defaultProps = {
  cin7CachedProducts: undefined,
};

export default SkuPicker;
